import { useState, useCallback } from 'react';

export const useZoom = (gridRef) => {
  const [zoom, setZoom] = useState(1);
  const [viewOffset, setViewOffset] = useState({ x: 0, y: 0 });

  const handleZoom = useCallback(
    (delta, clientX, clientY) => {
      const oldZoom = zoom;
      const newZoom = Math.max(0.1, Math.min(5, oldZoom + delta));

      if (newZoom !== oldZoom && gridRef.current) {
        const rect = gridRef.current.getBoundingClientRect();
        // Distance from visual top-left of transformed element
        const mouseX = clientX !== undefined ? clientX - rect.left : rect.width / 2;
        const mouseY = clientY !== undefined ? clientY - rect.top : rect.height / 2;

        // Keep content under cursor stable.
        // With transform on gridRef: rect already includes translate; visual = content * zoom.
        const scaleFactor = newZoom / oldZoom;
        const newOffsetX = viewOffset.x + mouseX * (1 - scaleFactor);
        const newOffsetY = viewOffset.y + mouseY * (1 - scaleFactor);

        setZoom(newZoom);
        setViewOffset({ x: newOffsetX, y: newOffsetY });
      }
    },
    [zoom, viewOffset, gridRef],
  );

  const handleWheel = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaY > 0 ? -0.15 : 0.15;
        handleZoom(delta, e.clientX, e.clientY);
      }
    },
    [handleZoom],
  );

  const resetZoom = useCallback(() => {
    setZoom(1);
    setViewOffset({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    if (gridRef.current) {
      const rect = gridRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      handleZoom(0.2, centerX, centerY);
    }
  }, [handleZoom, gridRef]);

  const zoomOut = useCallback(() => {
    if (gridRef.current) {
      const rect = gridRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      handleZoom(-0.2, centerX, centerY);
    }
  }, [handleZoom, gridRef]);

  return {
    zoom,
    viewOffset,
    setViewOffset,
    handleZoom,
    handleWheel,
    zoomIn,
    zoomOut,
    resetZoom,
  };
};
